import { forwardRef } from 'react';
import { ChevronDownIcon, Cross2Icon } from '@radix-ui/react-icons';

import type { ComboboxTriggerProps } from './combobox.types';

export const ComboboxTrigger = forwardRef<HTMLButtonElement, ComboboxTriggerProps>(
  (
    {
      isOpen,
      disabled,
      loading,
      error,
      hasValue,
      size = 'md',
      variant = 'outline',
      placeholder = 'Select option...',
      children,
      onClick,
      onClear,
      clearable = false,
      ...ariaProps
    },
    ref
  ) => {
    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      onClear?.();
    };

    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled || loading}
        onClick={onClick}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          boxSizing: 'border-box',
          borderRadius: '6px',
          transition: 'all 0.2s',
          outline: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
          ...(size === 'sm'
            ? { height: '32px', padding: '6px 8px', fontSize: '12px' }
            : size === 'lg'
              ? { height: '48px', padding: '10px 16px', fontSize: '16px' }
              : { height: '40px', padding: '8px 12px', fontSize: '14px' }),
          ...(variant === 'outline'
            ? { border: '1px solid #d1d5db', backgroundColor: '#ffffff' }
            : variant === 'filled'
              ? { border: '1px solid transparent', backgroundColor: '#f3f4f6' }
              : variant === 'flushed'
                ? {
                    border: '0',
                    borderBottom: '2px solid #d1d5db',
                    borderRadius: '0',
                    padding: '0',
                  }
                : { border: '0', backgroundColor: 'transparent', boxShadow: 'none' }),
          ...(error ? { border: '1px solid #ef4444' } : {}),
          ...(disabled ? { backgroundColor: '#f3f4f6', opacity: '0.5' } : {}),
        }}
        {...ariaProps}
      >
        <span
          style={{
            flex: 1,
            textAlign: 'left',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {children || <span style={{ color: '#9ca3af' }}>{placeholder}</span>}
        </span>

        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
          {loading && (
            <span
              style={{
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                border: '2px solid #e5e7eb',
                borderTop: '2px solid #3b82f6',
                animation: 'spin 1s linear infinite',
              }}
              data-testid="loading-spinner"
              aria-label="Loading"
            />
          )}

          {clearable && hasValue && !loading && !disabled && (
            <span
              role="button"
              onClick={handleClear}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClear(e as unknown as React.MouseEvent);
                }
              }}
              style={{
                padding: '2px',
                borderRadius: '4px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              aria-label="Clear selection"
              tabIndex={-1}
            >
              <Cross2Icon style={{ height: '12px', width: '12px' }} />
            </span>
          )}

          <ChevronDownIcon
            style={{
              height: '16px',
              width: '16px',
              transition: 'transform 0.2s',
              transform: isOpen ? 'rotate(180deg)' : 'none',
            }}
            aria-hidden="true"
          />
        </span>
      </button>
    );
  }
);

ComboboxTrigger.displayName = 'ComboboxTrigger';
